import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../stores/auth";
import { apiClient } from "../api";
import { buildTourBaseConfig, ensureFullscreenDummy, flashPopover } from "./tour-config";
import { clearTourState, loadTourState, saveTourState } from "./tour-state";
import { buildDeckTourSteps } from "./tour-steps.deck";
import "./tour.css";

const localFallbackKey = (userId: string) => `ai-deck-onboarding-seen-${userId}`;

const SERVICES_PATH = "/services";
const DASHBOARD_PATH = "/dashboard";

// ステップ index（0 始まり）
// 0: welcome, 1: services-nav, 2: favorite-toggle, 3: tag-filter, 4: search-bar,
// 5: service-logo, 6: service-memo, 7: dnd-handle, 8: user-menu
const IDX_SERVICES_NAV = 1;
const IDX_FAVORITE = 2;
const IDX_SEARCH = 4;
// HomePage に戻った直後の最初のステップ。/services から /dashboard への遷移、
// 逆方向の戻る操作はこの index を境界に行う
const IDX_SERVICE_LOGO = 5;

// 各ステップが想定するページ。ServicesPage で実行するステップは 2-4、それ以外は HomePage
const pageForStep = (step: number): string =>
  step >= IDX_FAVORITE && step <= IDX_SEARCH ? SERVICES_PATH : DASHBOARD_PATH;

export const useDeckTour = () => {
  const { t } = useTranslation();
  const { user, updateUser } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const startDeckTour = useCallback(
    (options?: { startStep?: number }) => {
      const startIndex = options?.startStep ?? 0;

      // 現在のパスがそのステップの想定ページと違う場合は遷移して、
      // 受け側ページの useEffect が resume するように sessionStorage に状態保存
      const expectedPath = pageForStep(startIndex);
      if (location.pathname !== expectedPath) {
        saveTourState({ tour: "deck", startStep: startIndex });
        navigate(expectedPath);
        return;
      }

      ensureFullscreenDummy();

      const driverObj = driver({
        ...buildTourBaseConfig(t),
        steps: buildDeckTourSteps(t),
        onPopoverRender: flashPopover,
        onNextClick: (_element, _step, opts) => {
          const idx = opts.state.activeIndex ?? 0;
          // services-nav で「次へ」 → /services へ遷移してステップ 3 から再開
          if (idx === IDX_SERVICES_NAV) {
            saveTourState({ tour: "deck", startStep: IDX_FAVORITE });
            driverObj.destroy();
            navigate(SERVICES_PATH);
            return;
          }
          // search-bar で「次へ」 → /dashboard へ戻って service-logo (idx 5) から再開
          if (idx === IDX_SEARCH) {
            saveTourState({ tour: "deck", startStep: IDX_SERVICE_LOGO });
            driverObj.destroy();
            navigate(DASHBOARD_PATH);
            return;
          }
          driverObj.moveNext();
        },
        onPrevClick: (_element, _step, opts) => {
          const idx = opts.state.activeIndex ?? 0;
          // favorite で「戻る」 → /dashboard でステップ 2 から再開
          if (idx === IDX_FAVORITE) {
            saveTourState({ tour: "deck", startStep: IDX_SERVICES_NAV });
            driverObj.destroy();
            navigate(DASHBOARD_PATH);
            return;
          }
          // service-logo で「戻る」 → /services の search-bar から再開
          if (idx === IDX_SERVICE_LOGO) {
            saveTourState({ tour: "deck", startStep: IDX_SEARCH });
            driverObj.destroy();
            navigate(SERVICES_PATH);
            return;
          }
          driverObj.movePrevious();
        },
        onDestroyed: async () => {
          // 途中遷移中なら sessionStorage に状態が残る → 完了処理スキップ
          if (loadTourState()) return;

          if (!user?.id) return;
          try {
            localStorage.setItem(localFallbackKey(user.id), "true");
            const response = await apiClient.completeOnboarding();
            if (response?.completedAt) {
              updateUser({ onboardingCompletedAt: response.completedAt });
            }
          } catch {
            // localStorage 救済済み
          }
        },
      });
      driverObj.drive(startIndex);
    },
    [t, user?.id, updateUser, navigate, location.pathname],
  );

  // 外部から「resume 中なら state をクリアして起動」したい場合に使う
  const resumeIfPending = useCallback(
    (expectedStartStep: number) => {
      const state = loadTourState();
      if (state?.tour !== "deck" || state.startStep !== expectedStartStep) return false;
      clearTourState();
      startDeckTour({ startStep: expectedStartStep });
      return true;
    },
    [startDeckTour],
  );

  return { startDeckTour, resumeIfPending };
};
